import { useTranslation } from 'react-i18next';
import { Box, Autocomplete, TextField, Typography, Slider } from '@mui/material';
import { getConfidenceLevel, getProbabilityColor } from '../../Prediction/utils';
import { MaterialConditionDTO } from '../../../api/generated';
import { MaterialOption } from '../types';

interface MaterialConditionInputProps {
  condition: MaterialConditionDTO;
  onChange: (condition: MaterialConditionDTO) => void;
  availableClasses: MaterialOption[];
}

export default function MaterialConditionInput({ condition, onChange, availableClasses }: MaterialConditionInputProps) {
  const { t } = useTranslation();

  const value = availableClasses.find((option) => option.className === condition.className) ?? null;
  const color = getProbabilityColor(getConfidenceLevel(condition.probability));

  const hasInvalidClass = !!condition.className && value === null;
  const errorMessage = hasInvalidClass ? t('billableMaterial.materialTypeError') : '';

  const handleChangeProbability = (_: Event, newValue: number | number[]) => {
    const prob = Array.isArray(newValue) ? newValue[0] : newValue;
    onChange({ ...condition, probability: prob });
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '3em' }}>
      <Autocomplete
        size='small'
        sx={{ width: '20em' }}
        value={value}
        options={availableClasses}
        getOptionLabel={(option) => (option.ewcClass ? `${option.ewcClass} - ${t(option.label)}` : t(option.label))}
        onChange={(_, val) => {
          if (val) onChange({ ...condition, className: val.className });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant='outlined'
            label={t('billableMaterial.conditionTypes.material')}
            error={Boolean(errorMessage)}
            helperText={errorMessage}
            slotProps={{
              formHelperText: {
                sx: {
                  position: 'absolute',
                  top: '100%',
                  whiteSpace: 'nowrap',
                  margin: 0,
                  left: '1rem',
                  width: 'max-content',
                  fontSize: '0.75rem',
                },
              },
            }}
          />
        )}
        disableClearable={!!value}
      />
      <Box>
        <Typography variant='body2'>
          {t('billableMaterial.minConfidence')} &ge; {condition.probability * 100}%
        </Typography>
        <Box sx={{ display: 'flex', gap: '0.5em' }}>
          <Slider
            size='small'
            value={condition.probability}
            onChange={handleChangeProbability}
            min={0.5}
            max={1.0}
            step={null}
            marks={[
              { value: 0.5, label: t('billableMaterial.medium') },
              { value: 0.9, label: t('billableMaterial.high') },
            ]}
            sx={{
              color: color,
              height: 6,
              '& .MuiSlider-markLabel': { fontSize: '0.7rem' },
              '& .MuiSlider-track': {
                height: 6,
                border: 'none',
                bgcolor: 'grey.300',
              },
              '& .MuiSlider-rail': {
                height: 6,
                opacity: 1,
                bgcolor: color,
              },
              '& .MuiSlider-thumb': {
                height: 20,
                width: 20,
                backgroundColor: '#fff',
                border: '2px solid currentColor',
                '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                  boxShadow: 'inherit',
                },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
